import { useMemo, useState } from 'react';
import { Box, Stack, Typography } from 'src/components/mui';

import { useMenuContext } from '../../context';
import MenuItem from './MenuItem';
import MenuSection from './MenuSection';
import { TIoTDevice, TIoTDeviceType, TIoTGroup } from './types';
import { CameraIcon, LockdownIcon, LockedIcon, UnlockedIcon } from './icons';

enum ETab {
  All = 'All',
  Favorites = 'Favorites'
}

type Props = {
  devicesTypes?: TIoTDeviceType[];
  groups?: TIoTGroup[];
  searchText: string;
  devices: TIoTDevice[];
  handleLock?: (d: TIoTDevice) => void;
  handleOpenCamera?: ({
    multiCams,
    openCamera
  }: {
    openCamera?: TIoTDevice;
    multiCams?: { id: number; tourId: number; name?: string; cameras: number[] | null };
  }) => void;
  handleMoveToLock?: (d: TIoTDevice) => void;
  providers: any[];
  favouriteCameras?: { id: number; tourId: number; name?: string; cameras: number[] | null }[];
};

export const MenuItems = (props: Props) => {
  const {
    providers,
    groups,
    searchText,
    handleLock,
    handleMoveToLock,
    handleOpenCamera,
    favouriteCameras
  } = props;
  const { colors } = useMenuContext();
  const [selectedProviderId, setSelectedProviderId] = useState<null | number>(null);
  const [tab, setTab] = useState(ETab.All);

  const devices = useMemo(
    () =>
      props.devices.filter((d) =>
        d?.name
          ? d?.name?.toLowerCase()?.includes(searchText.toLowerCase())
          : d?.externalDeviceId?.toLowerCase()?.includes(searchText.toLowerCase())
      ),
    [props.devices, searchText]
  );

  const ungroupedDevices = useMemo(() => devices.filter((d) => !d.groupId), [devices]);
  const favoriteDevices = useMemo(
    () =>
      devices.filter((d) =>
        selectedProviderId
          ? d.providerId === selectedProviderId && (d.favorite || d.isFavourite)
          : d.favorite || d.isFavourite
      ),
    [devices, selectedProviderId]
  );

  const renderItem = (d: TIoTDevice & { provider: any }, offsetLeft: number) => {
    return (
      <MenuItem
        key={`${d.id}_${d.name || d.externalDeviceId}`}
        label={d.name || d.externalDeviceId}
        offsetLeft={offsetLeft}
        icon={
          d?.provider?.provider?.name === 'kisi'
            ? d.lockedDown
              ? LockdownIcon
              : d.unlocked
              ? UnlockedIcon
              : LockedIcon
            : d?.provider?.provider?.name === 'rhombus'
            ? CameraIcon
            : undefined
        }
        onClick={() => handleMoveToLock?.(d)}
        onLock={() => {
          if (d?.provider?.provider?.name === 'kisi') {
            handleLock?.(d);
          }
          if (d?.provider?.provider?.name === 'rhombus') {
            handleOpenCamera?.({ openCamera: d });
          }
        }}
      />
    );
  };

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Stack
          direction="row"
          spacing={2}
          p="10px 14px"
          mb={2}
          sx={{
            overflowX: 'scroll',
            '&::-webkit-scrollbar': {
              height: '6px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: colors?.scrollbar || 'transparent',
              borderRadius: '2px'
            }
          }}
        >
          {providers?.map((provider) => {
            return (
              <Stack
                key={provider?.providerId}
                alignItems="center"
                justifyContent="center"
                sx={{
                  p: '8px 14px',
                  borderRadius: '50px',
                  border: `1px solid ${
                    selectedProviderId === provider?.providerId ? colors.iotText : '#fff'
                  }`,
                  whiteSpace: 'nowrap',
                  height: '36px'
                }}
              >
                <Stack
                  onClick={() =>
                    setSelectedProviderId((prev) =>
                      prev === provider.providerId ? undefined : provider?.providerId
                    )
                  }
                  direction="row"
                  gap={1}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    cursor: 'pointer',
                    color: selectedProviderId === provider?.providerId ? colors.iotText : '#FFFFFF'
                  }}
                >
                  {provider?.icon ? (
                    <img
                      src={`${provider?.icon}?hex=${encodeURIComponent(
                        selectedProviderId === provider?.providerId
                          ? colors.iotText || '#fff'
                          : '#FFFFFF'
                      )}`}
                      width="20px"
                      height="20px"
                    />
                  ) : (
                    <></>
                  )}
                  {provider?.name || provider?.provider?.name}
                </Stack>
              </Stack>
            );
          })}
        </Stack>

        <Stack
          sx={{
            px: '14px',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Results</Typography>
          <Stack direction="row" spacing={2}>
            <Stack
              onClick={() => setTab(ETab.All)}
              sx={{
                cursor: 'pointer',
                p: '8px',
                gap: '8px',
                borderRadius: '80px',
                border: `1px solid ${tab === ETab.All ? colors?.iotText : '#EAECF0'}`,
                width: '36px',
                height: '36px',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i
                className="fa-regular fa-bars-filter"
                style={{ fontSize: '18px', color: tab === ETab.All ? colors?.iotText : '#fff' }}
              />
            </Stack>
            <Stack
              onClick={() => setTab(ETab.Favorites)}
              sx={{
                cursor: 'pointer',
                p: '8px',
                gap: '8px',
                borderRadius: '80px',
                border: `1px solid ${tab === ETab.Favorites ? colors?.iotText : '#EAECF0'}`,
                width: '36px',
                height: '36px',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i
                className="fa-regular fa-star"
                style={{
                  fontSize: '18px',
                  color: tab === ETab.Favorites ? colors?.iotText : '#fff'
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <Box
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          mt: 1,
          mb: 2,
          '&::-webkit-scrollbar-track': {
            borderRadius: '2px',
            background: '#828282'
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '2px',
            background: colors.scrollbar
          },
          '&::-webkit-scrollbar': {
            width: '8px',
            borderRadius: '2px',
            background: '#828282'
          }
        }}
      >
        {tab === ETab.All ? (
          <>
            {providers
              ?.filter((provider) =>
                selectedProviderId ? provider?.providerId === selectedProviderId : provider
              )
              ?.map((provider) => {
                return (
                  <>
                    {groups
                      ?.filter((g) => g.providerId === provider?.providerId)
                      ?.map((g) => {
                        const groupHasDevices = devices.some((d) => d.groupId === g.id);
                        return (
                          groupHasDevices && (
                            <MenuSection
                              key={`${g.id}_${g.name}`}
                              label={g.name}
                              gutterLeft={30}
                              uncollapse={!!searchText}
                            >
                              {devices
                                .filter((fd) => fd.groupId === g.id)
                                .map((d) => renderItem({ ...d, provider }, 45))}
                            </MenuSection>
                          )
                        );
                      })}

                    {ungroupedDevices
                      .filter((ud) => ud.providerId === provider?.providerId)
                      .map((d) =>
                        renderItem(
                          {
                            ...d,
                            provider
                          },
                          30
                        )
                      )}
                  </>
                );
              })}
          </>
        ) : (
          <>
            {favouriteCameras?.map((camGroup, i) => {
              return (
                <MenuItem
                  key={camGroup.id}
                  label={camGroup.name || `Multi-Cam View ${i + 1}`}
                  offsetLeft={30}
                  icon={CameraIcon}
                  onClick={() => {}}
                  onLock={() => {
                    if (camGroup?.cameras) handleOpenCamera?.({ multiCams: camGroup });
                  }}
                />
              );
            })}
            {favoriteDevices.map((d) =>
              renderItem(
                {
                  ...d,
                  provider: providers.find((provider) => provider.providerId === d.providerId)
                },
                30
              )
            )}
          </>
        )}
      </Box>
    </>
  );
};
